import { ChevronRight, UserRound } from "lucide-react";
import { Link, useLocation } from "react-router";

import { NAV_ENTRIES, NAV_GROUPS, type NavEntry } from "@/app/shell/nav";
import { usePageTitle } from "@/shared/hooks/useDocumentTitle";
import { useT } from "@/shared/i18n/useT";

const PROFILE: NavEntry = {
  id: "me",
  to: "/me",
  labelId: "shell.profile",
  icon: UserRound,
  group: "overview",
};

/** The nav entry a path belongs to: the longest matching prefix ("/units/12" → Units). */
function entryFor(pathname: string): NavEntry | undefined {
  return [...NAV_ENTRIES, PROFILE]
    .filter((entry) =>
      entry.to === "/"
        ? pathname === "/"
        : pathname === entry.to || pathname.startsWith(`${entry.to}/`),
    )
    .sort((a, b) => b.to.length - a.to.length)[0];
}

/**
 * Where you are, kept in view while the page scrolls: section › page › record. On a record the
 * page crumb links back to its list.
 */
export function Breadcrumb() {
  const t = useT();
  const { pathname } = useLocation();
  const pageTitle = usePageTitle();
  const entry = entryFor(pathname);
  if (!entry) return null;

  const group = NAV_GROUPS.find((item) => item.id === entry.group)?.labelId;
  const label = t(entry.labelId);
  const deeper = pathname !== entry.to && entry.to !== "/me";
  const record = deeper && pageTitle && pageTitle !== label ? pageTitle : null;

  return (
    <nav aria-label={t("shell.breadcrumb")} className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1.5 text-[13.5px]">
        <li className="shrink-0">
          <span className="grid size-7 place-items-center rounded-lg bg-surface text-brand-strong shadow-xs ring-1 ring-line">
            <entry.icon className="size-3.5" aria-hidden="true" />
          </span>
        </li>
        {group ? (
          <>
            <li className="ms-1 hidden shrink-0 text-ink-subtle xl:block">{t(group)}</li>
            <li aria-hidden="true" className="hidden shrink-0 text-ink-faint xl:block">
              <ChevronRight className="size-3.5 rtl:-scale-x-100" />
            </li>
          </>
        ) : null}
        <li className="shrink-0">
          {record ? (
            <Link
              to={entry.to}
              className="rounded-md px-1 font-medium text-ink-muted transition-colors hover:text-ink"
            >
              {label}
            </Link>
          ) : (
            <span aria-current="page" className="px-1 font-semibold text-ink">
              {label}
            </span>
          )}
        </li>
        {record ? (
          <>
            <li aria-hidden="true" className="shrink-0 text-ink-faint">
              <ChevronRight className="size-3.5 rtl:-scale-x-100" />
            </li>
            <li
              aria-current="page"
              className="user-text min-w-0 truncate px-1 font-semibold text-ink"
            >
              {record}
            </li>
          </>
        ) : null}
      </ol>
    </nav>
  );
}
